import { Component, inject, signal } from '@angular/core';
import { DomSanitizer, Meta, SafeHtml, Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { LandingPageService } from '../../services/landing.service';
import { HostingService } from '../../services/hosting.service';
import {LandingPageData} from '../../models/landing-page.model';

@Component({
  selector: 'landing-public',
  imports: [],
  templateUrl: './landing-public.component.html',
  styleUrl: './landing-public.component.css'
})
export class LandingPublicComponent {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private sanitizer = inject(DomSanitizer);
  private titleService = inject(Title);
  private metaService = inject(Meta);
  private landingService = inject(LandingPageService);
  private hostingService = inject(HostingService);

  readonly pageData = signal<LandingPageData | null>(null);
  readonly sanitizedCustomHtml = signal<SafeHtml>('');
  readonly loading = signal<boolean>(true);
  readonly error = signal<boolean>(false);

  ngOnInit(): void {
    const slug = this.route.snapshot.params['slug'];
    const orgId = this.route.snapshot.params['org_id'];
    console.log('🔍 Cargando página con slug:', slug, 'org_id:', orgId);
    this.loadPage(slug, orgId);
  }

  private loadPage(slug: string, orgId?: string): void {
    this.landingService.getPageBySlug(slug).subscribe({
      next: (data: LandingPageData) => {
        console.log('✅ Página cargada:', data);

        this.pageData.set(data);

        // Sanitizar HTML personalizado
        if (data.custom_html) {
          this.sanitizedCustomHtml.set(
            this.sanitizer.bypassSecurityTrustHtml(data.custom_html)
          );
        }

        // Configurar SEO
        this.setupSEO(data);
        this.loading.set(false);
      },
      error: (err) => {
        console.error('❌ Error cargando página:', err);
        this.error.set(true);
        this.loading.set(false);
      }
    });
  }

  private setupSEO(data: LandingPageData): void {
    // Title
    this.titleService.setTitle(data.seo_tags.meta_title || data.title);

    // Meta Description
    this.metaService.updateTag({
      name: 'description',
      content: data.seo_tags.meta_description
    });

    // Keywords
    if (data.seo_tags.keywords.length > 0) {
      this.metaService.updateTag({
        name: 'keywords',
        content: data.seo_tags.keywords.join(', ')
      });
    }

    // Open Graph
    this.metaService.updateTag({
      property: 'og:title',
      content: data.seo_tags.meta_title
    });

    this.metaService.updateTag({
      property: 'og:description',
      content: data.seo_tags.meta_description
    });

    if (data.seo_tags.og_image) {
      this.metaService.updateTag({
        property: 'og:image',
        content: data.seo_tags.og_image
      });
    }
  }

  // Método para redirigir a reservas
  goToBooking(): void {
    const data = this.pageData();
    if (!data) {
      console.error('❌ No hay datos de la página');
      alert('Error: No se cargaron los datos de la página');
      return;
    }

    console.log('🔍 Datos de landing page:', data);

    // PRIORIDAD 1: Buscar accommodation_id directo
    const accommodationId = data.hosting_id || data.accommodation_id;

    if (accommodationId) {
      console.log('✅ ID de alojamiento encontrado directamente:', accommodationId);
      this.navegarAReserva(data, accommodationId);
      return;
    }

    // PRIORIDAD 2: Buscar por space_id o room_id
    const spaceId = data.space_id || data.room_id;
    if (spaceId) {
      console.log('⚠️ Buscando por space_id:', spaceId);
      this.buscarAlojamientoPorSpace(data, spaceId);
      return;
    }

    // PRIORIDAD 3: Buscar por slug de la landing page
    console.log('⚠️ Buscando por slug de landing page:', data.slug);
    this.buscarAlojamientoPorSlug(data);
  }

  private async buscarAlojamientoPorSpace(data: LandingPageData, spaceId: string): Promise<void> {
    try {
      const allHostings = await this.hostingService.getAllHostings();
      console.log('📦 Todos los alojamientos cargados:', allHostings);

      // Buscar alojamiento que contenga el space_id en sus rooms
      const hostingConSpace = allHostings.find(h => {
        if (Array.isArray(h.rooms)) {
          return h.rooms.some((room: any) => room.id === spaceId || room.room_id === spaceId);
        }
        return false;
      });

      if (hostingConSpace) {
        console.log('✅ Alojamiento encontrado para space_id:', hostingConSpace.id);
        this.navegarAReserva(data, hostingConSpace.id);
        return;
      }

      // Si no encuentra por space_id, buscar por org_id
      console.log('⚠️ No encontrado por space_id, buscando por org_id:', data.org_id);
      this.buscarAlojamientoPorOrgId(data);
    } catch (error: any) {
      console.error('❌ Error buscando por space_id:', error);
      this.buscarAlojamientoPorOrgId(data);
    }
  }

  private async buscarAlojamientoPorSlug(data: LandingPageData): Promise<void> {
    try {
      const allHostings = await this.hostingService.getAllHostings();
      console.log('📦 Todos los alojamientos cargados:', allHostings);

      // Buscar por slug o nombre que coincida
      const hostingConSlug = allHostings.find(h => {
        if (h.name && h.name.toLowerCase().includes(data.slug.toLowerCase())) {
          return true;
        }
        if (Array.isArray(h.rooms)) {
          return h.rooms.some((room: any) =>
            room.name?.toLowerCase().includes(data.slug.toLowerCase()) ||
            room.type?.toLowerCase().includes(data.slug.toLowerCase())
          );
        }
        return false;
      });

      if (hostingConSlug) {
        console.log('✅ Alojamiento encontrado para slug:', hostingConSlug.id);
        this.navegarAReserva(data, hostingConSlug.id);
        return;
      }

      // Si no encuentra por slug, buscar por org_id
      console.log('⚠️ No encontrado por slug, buscando por org_id:', data.org_id);
      this.buscarAlojamientoPorOrgId(data);
    } catch (error: any) {
      console.error('❌ Error buscando por slug:', error);
      this.buscarAlojamientoPorOrgId(data);
    }
  }

  private async buscarAlojamientoPorOrgId(data: LandingPageData): Promise<void> {
    try {
      const allHostings = await this.hostingService.getAllHostings();
      console.log('📦 Todos los alojamientos cargados:', allHostings);
      console.log('🔍 Filtrando por org_id:', data.org_id);

      // Intentar buscar por org_id primero
      let hostingsPorOrg = allHostings.filter(h =>
        h.organization_id === data.org_id ||
        h.org_id === data.org_id ||
        (h as any).tenant_id === data.org_id
      );

      console.log(`📦 Encontrados ${hostingsPorOrg.length} alojamientos por org_id exacto`);

      // Si no encuentra por org_id exacto, buscar por nombre (org_id a menudo es un slug basado en el nombre)
      if (!hostingsPorOrg || hostingsPorOrg.length === 0) {
        console.log('🔄 Buscando por nombre del alojamiento (org_id como slug)...');

        // Separar org_id en palabras para buscar
        const orgIdPalabras = data.org_id
          .toLowerCase()
          .split(/[-_\s]+/)
          .filter(p => p.length > 0);

        console.log('🔤 Palabras clave del org_id:', orgIdPalabras);

        hostingsPorOrg = allHostings.filter(h => {
          if (!h.name) return false;

          const nombrePalabras = h.name
            .toLowerCase()
            .split(/[-_\s]+/)
            .filter(p => p.length > 0);

          // Buscar si alguna palabra del org_id coincide con alguna palabra del nombre
          return orgIdPalabras.some(palabra =>
            nombrePalabras.some(nombrePalabra =>
              nombrePalabra.includes(palabra) || palabra.includes(nombrePalabra)
            )
          );
        });

        console.log(`📦 Encontrados ${hostingsPorOrg.length} alojamientos por nombre`);
        console.log('🏨 Alojamientos encontrados:', hostingsPorOrg.map(h => h.name));
      }

      console.log(`📋 Alojamientos encontrados para org_id: ${data.org_id}`, hostingsPorOrg);

      if (!hostingsPorOrg || hostingsPorOrg.length === 0) {
        console.error('❌ No hay alojamientos para esta organización:', data.org_id);
        console.error('💡 Sugerencia: Verifique que org_id existe en los datos del alojamiento');
        alert('❌ No se encontró alojamiento disponible. Por favor contacte con soporte.');
        return;
      }

      // Usar el primer alojamiento disponible
      const firstHosting = hostingsPorOrg[0];
      const accommodationId = firstHosting.id;
      console.log('✅ Usando alojamiento encontrado:', {
        nombre: firstHosting.name,
        id: accommodationId
      });
      this.navegarAReserva(data, accommodationId);
    } catch (error: any) {
      console.error('❌ Error buscando alojamientos por org_id:', error);
      alert('Error al buscar alojamientos disponibles');
    }
  }

  private navegarAReserva(data: LandingPageData, accommodationId: string): void {
    const organizationName = data.content_json?.hero?.title || data.title;

    const queryParams: any = {
      org: data.org_id,
      landing: data.slug,
      name: organizationName,
      accommodation_id: accommodationId
    };

    console.log('📍 Navegando con parámetros:', queryParams);
    this.router.navigate(['/reservas/invitado'], { queryParams });
  }

  get hasCustomHtml(): boolean {
    const data = this.pageData();
    return !!data?.custom_html && data.custom_html.trim() !== '';
  }
}

